import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["codeText", "editButton", "codeInput", "saveButton", "cancelButton"]
  static values = { 
    updateUrl: String,
    getCurrentCodeUrl: String,
    modelId: Number,
    currentCode: String
  }

  connect() {
    this.isEditing = false
  }

  async edit() {
    if (this.isEditing) return

    // Показываем индикатор загрузки на кнопке "Изменить"
    const originalEditText = this.editButtonTarget.innerHTML
    const loadingText = this.editButtonTarget.dataset.loadingText || 'Loading...'
    this.editButtonTarget.innerHTML = `<i class="fa-solid fa-spinner fa-spin me-1"></i>${loadingText}`
    this.editButtonTarget.disabled = true

    try {
      // Загружаем актуальный код с сервера
      const currentCode = parseInt(await this.fetchCurrentCode())

      this.isEditing = true
      
      // Показываем поле ввода и кнопки сохранения/отмены
      this.codeTextTarget.style.display = 'none'
      this.editButtonTarget.style.display = 'none'
      
      this.codeInputTarget.style.display = 'inline-block'
      this.saveButtonTarget.style.display = 'inline-block'
      this.cancelButtonTarget.style.display = 'inline-block'
      
      // Устанавливаем актуальное значение кода в поле ввода
      this.codeInputTarget.value = currentCode || ''
      this.codeInputTarget.focus()
      this.codeInputTarget.select()
      
      // Если код изменился, показываем уведомление
      if (currentCode !== this.currentCodeValue) {        
        // this.showNotification('Код был обновлен другим пользователем. Отображается актуальная версия.', 'info')
        this.currentCodeValue = currentCode
        // console.log('Code was updated by another user. Current version is displayed.', this.currentCodeValue, this)
      }
    } catch (error) {
      console.error('Error fetching current code:', error)
      // В случае ошибки используем локальное значение
      this.isEditing = true
      
      this.codeTextTarget.style.display = 'none'
      this.editButtonTarget.style.display = 'none'
      
      this.codeInputTarget.style.display = 'inline-block'
      this.saveButtonTarget.style.display = 'inline-block'
      this.cancelButtonTarget.style.display = 'inline-block'
      
      this.codeInputTarget.value = this.currentCodeValue || ''
      this.codeInputTarget.focus()
      this.codeInputTarget.select()
      
      this.showNotification('Не удалось загрузить актуальный код. Используется локальная версия.', 'warning')
    } finally {
      // Восстанавливаем кнопку редактирования
      this.editButtonTarget.innerHTML = originalEditText
      this.editButtonTarget.disabled = false
    }
  }

  cancel() {
    if (!this.isEditing) return

    this.isEditing = false
    
    // Возвращаем исходное отображение
    this.codeTextTarget.style.display = 'inline'
    this.editButtonTarget.style.display = 'inline-block'
    
    this.codeInputTarget.style.display = 'none'
    this.saveButtonTarget.style.display = 'none'
    this.cancelButtonTarget.style.display = 'none'
    
    // Сбрасываем значение поля ввода
    // console.log('Resetting input value to:', this.currentCodeValue, this)
    this.codeInputTarget.value = this.currentCodeValue || ''
    this.updateCurrentCodeValue(this.currentCodeValue || '')
  }

  async save() {
    if (!this.isEditing) return

    const newCode = this.codeInputTarget.value.trim()
    
    // Подготавливаем данные для отправки
    const formData = new FormData()
    formData.append('code', newCode)
    formData.append('model_id', this.modelIdValue)
    
    try {
      // Показываем индикатор загрузки
      const originalSaveText = this.saveButtonTarget.innerHTML
      this.saveButtonTarget.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i>'
      this.saveButtonTarget.disabled = true
      
      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData
      })

      const result = await response.json()
      
      if (response.ok && result.success) {
        // Успешное обновление
        this.currentCodeValue = newCode
        
        // Обновляем отображение кода с учетом стилизации
        this.updateCurrentCodeValue(newCode)
        
        this.showNotification(result.message || 'Код успешно обновлен', 'success')
        this.cancel() // Выходим из режима редактирования
      } else {
        // Ошибка обновления
        const errorMessage = result.error || 'Произошла ошибка при обновлении кода'
        this.showNotification(errorMessage, 'error')
      }
    } catch (error) {
      console.error('Error updating code:', error)
      this.showNotification('Произошла ошибка при обновлении кода', 'error')
    } finally {
      // Восстанавливаем кнопку сохранения
      const saveText = this.saveButtonTarget.dataset.saveText || 'Сохранить'
      this.saveButtonTarget.innerHTML = `<i class="fa-solid fa-check me-1"></i>${saveText}`
      this.saveButtonTarget.disabled = false
    }
  }

  updateCurrentCodeValue(newCode) {
    if (newCode) {
        // Проверяем, есть ли элемент code в текущем отображении
        const existingCode = this.codeTextTarget.querySelector('code')
        if (existingCode) {
        existingCode.textContent = newCode
        } else {
        const existingStrong = this.codeTextTarget.querySelector('strong')
        if (existingStrong) {
            existingStrong.textContent = newCode
        } else {
            this.codeTextTarget.innerHTML = `<strong>${newCode}</strong>`
        }
        }
    } else {
        this.codeTextTarget.innerHTML = '<span class="text-muted">—</span>'
    }    
  }

  // Обработка нажатия Enter в поле ввода
  handleKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.save()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      this.cancel()
    }
  }

  async fetchCurrentCode() {
    try {
      const response = await fetch(this.getCurrentCodeUrlValue, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.success) {
        return result.code
      } else {
        throw new Error(result.error || 'Failed to fetch current code')
      }
    } catch (error) {
      console.error('Error fetching current code:', error)
      throw error
    }
  }

  showNotification(message, type = 'info') {
    const alertClass = type === 'success' ? 'alert-success' : 
                      type === 'error' ? 'alert-danger' : 
                      type === 'warning' ? 'alert-warning' : 'alert-info'
    
    const notification = document.createElement('div')
    notification.className = `alert ${alertClass} alert-dismissible fade show position-fixed`
    notification.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;'
    notification.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `
    
    document.body.appendChild(notification)
    
    // Автоматическое удаление через 5 секунд
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove()
      }
    }, 5000)
  }
}
